import { apiRequest } from '../../api';
import appConfig from '../../config/appConfig';
import { useRef, useEffect, useCallback, useState } from 'react';
import Map, { Marker, Popup, NavigationControl, ScaleControl, GeolocateControl } from 'react-map-gl/maplibre';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Layers as LayersIconMUI } from '@mui/icons-material';
import boundaryIcon from '../../assets/Boundary (1).svg';
import POIControl from './POIControl';
import Tools from './Tools';
import Search from './Search';
import GeoQuery from './GeoQuery';
import { computeListingScore, getScoreColor } from './SortingListings';
import { rankListingsByAiMatch } from './aiSearchSync';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import './MapContainer.css';
import './GeoQuery.css';

// ── Map style configs (unchanged) ─────────────────────────────────────────
const MAP_STYLES = {
    standard: {
        version: 8,
        sources: { 'osm': { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap' } },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }]
    },
    dark: {
        version: 8,
        sources: { 'carto-dark': { type: 'raster', tiles: ['https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© CARTO' } },
        layers: [{ id: 'carto-dark', type: 'raster', source: 'carto-dark' }]
    },
    satellite: {
        version: 8,
        sources: { 'esri-sat': { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: '© Esri' } },
        layers: [{ id: 'esri-sat', type: 'raster', source: 'esri-sat' }]
    },
    terrain3d: {
        version: 8,
        sources: {
            'osm': { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256 },
            'terrainSource': { type: 'raster-dem', tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'], tileSize: 256, encoding: 'terrarium' },
            'hillshadeSource': { type: 'raster-dem', tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'], tileSize: 256, encoding: 'terrarium' }
        },
        layers: [
            { id: 'osm', type: 'raster', source: 'osm' },
            { id: 'hills', type: 'hillshade', source: 'hillshadeSource', layout: { visibility: 'visible' }, paint: { 'hillshade-shadow-color': '#473B24', 'hillshade-exaggeration': 0.5 } }
        ],
        terrain: { source: 'terrainSource', exaggeration: 1.5 }
    },
    minimal: {
        version: 8,
        sources: {
            'osm-minimal': {
                type: 'raster',
                tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors'
            }
        },
        layers: [
            {
                id: 'minimal-background',
                type: 'background',
                paint: { 'background-color': '#ffffff' }
            },
            {
                id: 'osm-minimal',
                type: 'raster',
                source: 'osm-minimal',
                paint: {
                    'raster-saturation': -0.85,
                    'raster-contrast': 0.28,
                    'raster-brightness-min': 0.2,
                    'raster-brightness-max': 1,
                    'raster-opacity': 0.82
                }
            }
        ]
    }
};

export const OVERLAY_LAYERS = [
    { id: 'popdenpoints', name: 'تراکم جمعیت', workspace: 'BasicMaps', layerName: 'popdenpoints', geoserverUrl: appConfig.services.geoserverBaseURL },
    { id: 'geoServices_neighborhood', name: 'مرز محلات', workspace: 'BasicMaps', layerName: 'geoServices_neighborhood', geoserverUrl: appConfig.services.geoserverBaseURL },
    { id: 'ndvi', name: 'سبزینگی محیطی', workspace: 'BasicMaps', layerName: 'ndvi', geoserverUrl: appConfig.services.geoserverBaseURL },
    { id: 'ٌWalkability', name: 'پیاده مداری', workspace: 'BasicMaps', layerName: 'Walkability', geoserverUrl: appConfig.services.geoserverBaseURL },
    { id: 'popDensity', name: 'تراکم جمعیتی', workspace: 'BasicMaps', layerName: 'popDensity', geoserverUrl: appConfig.services.geoserverBaseURL }
];

// ── Helpers ────────────────────────────────────────────────────────────────

/** Approximate network distance factor (Euclidean → network) */
const NETWORK_DISTANCE_FACTOR = 0.6;

/**
 * Build a GeoJSON circle polygon approximation.
 * @param {number} lon  center longitude
 * @param {number} lat  center latitude
 * @param {number} radiusMeters
 * @param {number} steps  number of vertices
 */
function makeCirclePolygon(lon, lat, radiusMeters, steps = 48) {
    const coords = [];
    const earthR = 6371000;
    const dLat = (radiusMeters / earthR) * (180 / Math.PI);
    const dLon = dLat / Math.cos((lat * Math.PI) / 180);
    for (let i = 0; i <= steps; i++) {
        const angle = (2 * Math.PI * i) / steps;
        coords.push([lon + dLon * Math.cos(angle), lat + dLat * Math.sin(angle)]);
    }
    return coords; // closed ring [ [lon,lat], ... ]
}

function formatPrice(price, currency) {
    if (!price) return null;
    const n = parseFloat(price);
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}م`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toLocaleString();
}

// ── Property Marker ───────────────────────────────────────────────────────
function PropertyMarker({ listing, isSelected, onClick, scoreColor, rank }) {
    const isRent = listing.listing_type === 'RENT';
    const price = formatPrice(listing.price, listing.currency);
    return (
        <button
            className={`pm-marker ${isRent ? 'rent' : 'sale'} ${isSelected ? 'selected' : ''}`}
            onClick={onClick}
            title={listing.property?.name || ''}
            aria-label={`${listing.property?.name || 'ملک'}${rank ? `، رتبه ${rank}` : ''}${price ? `، ${price}` : ''}`}
            style={scoreColor ? { '--pm-score-color': scoreColor } : undefined}
        >
            <span className="pm-dot" style={scoreColor ? { background: scoreColor } : undefined} />
            {rank && <span className="pm-rank">#{rank}</span>}
            {price && <span className="pm-price">{price}</span>}
        </button>
    );
}

// ── Rich Property Popup ───────────────────────────────────────────────────
function PropertyPopup({ listing, onClose }) {
    const prop = listing.property || {};
    const isRent = listing.listing_type === 'RENT';
    const price = parseFloat(listing.price);
    const deposit = parseFloat(listing.deposit);

    return (
        <div className="pp-popup" dir="rtl">
            {prop.name && <div className="pp-name">{prop.name}</div>}
            <div className={`pp-chip ${isRent ? 'rent' : 'sale'}`}>{isRent ? 'رهن و اجاره' : 'فروش'}</div>

            <div className="pp-price">
                {price ? price.toLocaleString() : '—'} {listing.currency}
                {isRent && <span className="pp-price-unit">/ماه</span>}
            </div>

            {isRent && deposit > 0 && (
                <div className="pp-deposit">ودیعه: {deposit.toLocaleString()} {listing.currency}</div>
            )}

            <div className="pp-meta">
                {prop.area && <span>📐 {prop.area} م²</span>}
                {prop.neighborhood && <span>📍 {prop.neighborhood}</span>}
            </div>

            <button
                className="pp-cta"
                onClick={() => window.open(`/listing/${listing.listing_id}`, '_blank')}
            >
                مشاهده جزئیات ←
            </button>
        </div>
    );
}

// ── Compact Glass Toolbar ─────────────────────────────────────────────────
function MapToolbar({
    showSearch, setShowSearch,
    showLayerControl, setShowLayerControl,
    activeLayers, setActiveLayers,
    isDrawing, drawnArea,
    onStartDraw, onStopDraw, onClearDraw,
    mapRef, setViewState,
    onRoutingStateChange, onRouteDataChange,
    onDistanceSearchStateChange, onDistanceBufferChange,
    onGeoFilterChange,
    activeGeoFilters,
    onGeoQueryConditionsChange,
    onGeoQueryPOIsChange,
    onFlashBuffers,
}) {
    const closeAll = (except) => {
        if (except !== 'search') setShowSearch(false);
        if (except !== 'layers') setShowLayerControl(false);
    };

    return (
        <div className="mc-toolbar">
            {/* 1 — Search */}
            <Search
                mapRef={mapRef}
                setViewState={setViewState}
                showSearch={showSearch}
                setShowSearch={(v) => { closeAll('search'); setShowSearch(v); }}
                setShowLayerControl={setShowLayerControl}
                onGeoFilterChange={onGeoFilterChange}
                activeGeoFilters={activeGeoFilters}
            />

            <div className="mc-toolbar-divider" />

            {/* 2 — Layers */}
            <button
                className={`mc-tool-btn ${showLayerControl ? 'active' : ''}`}
                onClick={() => { const next = !showLayerControl; closeAll('layers'); setShowLayerControl(next); }}
                title="لایه‌ها"
                aria-pressed={showLayerControl}
            >
                <LayersIconMUI fontSize="small" />
                {activeLayers.length > 0 && <span className="mc-tool-badge">{activeLayers.length}</span>}
            </button>

            <div className="mc-toolbar-divider" />

            {/* 3 — Tools (routing / distance) */}
            <Tools
                mapRef={mapRef}
                setViewState={setViewState}
                onRoutingStateChange={onRoutingStateChange}
                onRouteDataChange={onRouteDataChange}
                onDistanceSearchStateChange={onDistanceSearchStateChange}
                onDistanceBufferChange={onDistanceBufferChange}
                setShowLayerControl={setShowLayerControl}
                setShowSearch={setShowSearch}
            />

            <div className="mc-toolbar-divider" />

            {/* 4 — Draw */}
            <div className="mc-draw-wrap">
                <button
                    className={`mc-tool-btn draw ${isDrawing ? 'active drawing-pulse' : ''} ${drawnArea && !isDrawing ? 'has-area' : ''}`}
                    onClick={isDrawing ? onStopDraw : onStartDraw}
                    title={isDrawing ? 'تمام ترسیم' : 'ترسیم محدوده'}
                    aria-pressed={isDrawing}
                >
                    <img src={boundaryIcon} alt="draw" style={{ width: 18, height: 18 }} />
                </button>
                {drawnArea && !isDrawing && (
                    <button className="mc-clear-area" onClick={onClearDraw} title="پاک کردن محدوده">
                        ×
                    </button>
                )}
            </div>

            <div className="mc-toolbar-divider" />

            {/* 5 — جستجوی مکانی هوشمند */}
            <GeoQuery
                onConditionsChange={onGeoQueryConditionsChange}
                onPOIsChange={onGeoQueryPOIsChange}
                onFlashBuffers={onFlashBuffers}
                setShowLayerControl={setShowLayerControl}
                setShowSearch={setShowSearch}
            />
        </div>
    );
}

// ── Drawing mode overlay banner ───────────────────────────────────────────
function DrawingBanner({ isDrawing, onCancel }) {
    if (!isDrawing) return null;
    return (
        <div className="mc-drawing-banner">
            <span className="mc-drawing-icon">✏️</span>
            <span>ماوس را روی نقشه بکشید تا محدوده ترسیم شود</span>
            <button className="mc-drawing-cancel" onClick={onCancel}>لغو</button>
        </div>
    );
}

// ── Zoom level badge ──────────────────────────────────────────────────────
function ZoomBadge({ zoom }) {
    return (
        <div className="mc-zoom-badge" title="سطح زوم">
            z{Math.round(zoom)}
        </div>
    );
}

// ── Base map switcher (row of thumbnail chips at bottom-right) ────────────
function BaseMapSwitcher({ currentMap, onMapChange }) {
    const [expanded, setExpanded] = useState(false);
    const ref = useRef(null);

    const options = [
        { id: 'standard', label: 'نقشه', emoji: '🗺️' },
        { id: 'satellite', label: 'ماهواره', emoji: '🛰️' },
        { id: 'dark', label: 'تاریک', emoji: '🌑' },
        { id: 'terrain3d', label: 'سه‌بعدی', emoji: '⛰️' },
        { id: 'minimal', label: 'ساده', emoji: '⬜' },
    ];

    useEffect(() => {
        if (!expanded) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setExpanded(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [expanded]);

    const current = options.find(o => o.id === currentMap);

    return (
        <div className="mc-basemap" ref={ref}>
            {expanded && (
                <div className="mc-basemap-options">
                    {options.map(o => (
                        <button
                            key={o.id}
                            className={`mc-basemap-chip ${currentMap === o.id ? 'active' : ''}`}
                            onClick={() => { onMapChange(o.id); setExpanded(false); }}
                        >
                            <span className="mc-basemap-emoji">{o.emoji}</span>
                            <span className="mc-basemap-label">{o.label}</span>
                            {currentMap === o.id && <span className="mc-basemap-check">✓</span>}
                        </button>
                    ))}
                </div>
            )}
            <button className="mc-basemap-toggle" onClick={() => setExpanded(v => !v)} title="تغییر نقشه پایه">
                <span>{current?.emoji}</span>
                <span className="mc-basemap-toggle-label">{current?.label}</span>
                <span className="mc-basemap-toggle-arrow">{expanded ? '▲' : '▼'}</span>
            </button>
        </div>
    );
}

// ── Layer panel ───────────────────────────────────────────────────────────
function LayerPanel({ activeLayers, setActiveLayers }) {
    return (
        <div className="mc-layer-panel">
            <div className="mc-layer-title">لایه‌های نقشه</div>
            {OVERLAY_LAYERS.map(layer => {
                const on = activeLayers.includes(layer.id);
                return (
                    <button
                        key={layer.id}
                        className={`mc-layer-row ${on ? 'on' : ''}`}
                        onClick={() => setActiveLayers(prev =>
                            on ? prev.filter(id => id !== layer.id) : [...prev, layer.id]
                        )}
                    >
                        <span className={`mc-layer-toggle ${on ? 'on' : ''}`}>
                            <span className="mc-layer-knob" />
                        </span>
                        <span className="mc-layer-name">{layer.name}</span>
                    </button>
                );
            })}
        </div>
    );
}

// ── Main MapContainer ─────────────────────────────────────────────────────
function MapContainer({
    viewState, setViewState,
    visibleProperties, selectedProperty, setSelectedProperty,
    filters, setFilters,
    isDrawing, setIsDrawing, drawingPoints, setDrawingPoints,
    isMouseDown, setIsMouseDown,
    drawnArea, setDrawnArea,
    distanceSearchState, distanceBuffer,
    onDistanceSearchStateChange, onDistanceBufferChange,
    filterProperties, mapKey,
    activeLayers, mapRef, properties,
    setVisibleProperties, setMapKey, setActiveLayers,
    showLayerControl, setShowLayerControl,
    onGeoFilterChange,
    sortPriorities = [],
    aiMatch,
}) {
    const drawRef = useRef(null);
    const suppressNextMoveEndRef = useRef(false);
    const [neighborhoodTooltip, setNeighborhoodTooltip] = useState(null);
    const [viewportDirty, setViewportDirty] = useState(false);
    const [showPOIs, setShowPOIs] = useState(false);
    const [pois, setPois] = useState([]);
    const [selectedPOI, setSelectedPOI] = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const [routingState, setRoutingState] = useState({ isActive: false, step: null });
    const [routeData, setRouteData] = useState(null);
    const handleRoutingClick = Tools.handleRoutingClick;
    const handleDistanceSearchClick = Tools.handleDistanceSearchClick;

    // New AI results may be outside the user's current viewport. Show the
    // complete ranked set first and fit the map around their coordinates.
    useEffect(() => {
        if (!aiMatch?.active || !mapRef.current || !properties.length) return undefined;
        const matched = rankListingsByAiMatch(properties, aiMatch);
        const coordinates = matched
            .map(item => item.property?.location?.coordinates)
            .filter(coords => Array.isArray(coords) && coords.length >= 2);
        setVisibleProperties(matched);
        if (coordinates.length === 0) return undefined;

        const map = mapRef.current.getMap();
        const fit = () => {
            if (coordinates.length === 1) {
                suppressNextMoveEndRef.current = true;
                map.easeTo({ center: coordinates[0], zoom: 15, duration: 700 });
                setViewportDirty(false);
                return;
            }
            const lons = coordinates.map(([lon]) => lon);
            const lats = coordinates.map(([, lat]) => lat);
            suppressNextMoveEndRef.current = true;
            map.fitBounds(
                [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
                { padding: 80, maxZoom: 15, duration: 700 },
            );
            setViewportDirty(false);
        };
        if (map.loaded()) fit();
        else map.once('load', fit);
        return () => map.off('load', fit);
    }, [aiMatch, properties, mapRef, setVisibleProperties]);

    // ── GeoQuery state ──────────────────────────────────────────
    const [geoQueryConditions, setGeoQueryConditions] = useState([]);  // [{ featureId, threshold, featureDef }]
    const [geoQueryPOIs, setGeoQueryPOIs] = useState([]);              // flat POI array for map markers

    // ── GeoQuery buffer flash state ─────────────────────────────
    // [{ key, rings, fading }]  — fades out after 3s
    const [flashBuffers, setFlashBuffers] = useState([]);
    const [poisFading, setPoisFading] = useState(false);   // drives POI dot opacity
    const flashTimers = useRef({});

    const handleFlashBuffers = useCallback((conditions) => {
        conditions.forEach(cond => {
            const { featureId, threshold, featureDef, pois } = cond;
            if (!pois?.length || !threshold) return;
            const isScore = featureDef?.feature_type === 'score';
            if (isScore) return;

            const radiusMeters = threshold * NETWORK_DISTANCE_FACTOR;
            const rings = pois.map(poi => {
                const coords = poi.geometry?.coordinates;
                if (!coords) return null;
                const [lon, lat] = Array.isArray(coords[0]) ? coords[0] : coords;
                return makeCirclePolygon(lon, lat, radiusMeters);
            }).filter(Boolean);

            if (!rings.length) return;

            const key = `flash-${featureId}-${Date.now()}`;

            // cancel any existing timers for this feature
            if (flashTimers.current[featureId]) {
                clearTimeout(flashTimers.current[featureId].fade);
                clearTimeout(flashTimers.current[featureId].remove);
                clearTimeout(flashTimers.current[featureId].poisFade);
                clearTimeout(flashTimers.current[featureId].poisRemove);
                setFlashBuffers(prev => prev.filter(b => !b.key.startsWith(`flash-${featureId}-`)));
            }

            // reset POI opacity in case a previous cycle had faded them
            setPoisFading(false);

            // add new buffer (visible)
            setFlashBuffers(prev => [...prev, { key, rings, fading: false }]);

            flashTimers.current[featureId] = {
                // t=2s: start buffer fade
                fade: setTimeout(() => {
                    setFlashBuffers(prev =>
                        prev.map(b => b.key === key ? { ...b, fading: true } : b)
                    );
                }, 2000),
                // t=3.2s: remove buffer from DOM, start POI fade
                remove: setTimeout(() => {
                    setFlashBuffers(prev => prev.filter(b => b.key !== key));
                    setPoisFading(true);
                }, 3200),
                // t=4.2s: POI fade complete — reset opacity for next flash
                poisRemove: setTimeout(() => {
                    setPoisFading(false);
                    delete flashTimers.current[featureId];
                }, 4200),
            };
        });
    }, []);

    // ── Map move ────────────────────────────────────────────────
    const handleMapMove = useCallback((ref) => {
        setMapKey(prev => prev + 1);
        if (ref?.current) {
            const map = ref.current.getMap();
            const bounds = map.getBounds();
            const visible = properties.filter(l => {
                const loc = l.property?.location;
                if (!loc?.coordinates) return false;
                const [lon, lat] = loc.coordinates;
                return bounds.contains([lon, lat]);
            });
            setVisibleProperties(visible);
        }
    }, [properties, setMapKey, setVisibleProperties]);

    // ── Draw init ───────────────────────────────────────────────
    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current.getMap();
        const draw = new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true }, defaultMode: 'simple_select' });
        map.addControl(draw, 'top-right');
        drawRef.current = draw;

        const onCreate = (e) => {
            const f = e.features[0];
            if (f.geometry.type === 'Polygon') setDrawnArea(f.geometry.coordinates[0]);
        };
        const onUpdate = (e) => {
            const f = e.features[0];
            if (f.geometry.type === 'Polygon') setDrawnArea(f.geometry.coordinates[0]);
        };
        const onDelete = () => setDrawnArea(null);

        map.on('draw.create', onCreate);
        map.on('draw.update', onUpdate);
        map.on('draw.delete', onDelete);

        return () => {
            map.off('draw.create', onCreate);
            map.off('draw.update', onUpdate);
            map.off('draw.delete', onDelete);
            if (drawRef.current) try { map.removeControl(drawRef.current); } catch { }
        };
    }, []); // eslint-disable-line

    // ── GeoServer layers ────────────────────────────────────────
    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current.getMap();
        const apply = (map) => {
            OVERLAY_LAYERS.forEach(layer => {
                const srcId = `geoserver-${layer.id}`;
                const lyrId = `geoserver-layer-${layer.id}`;
                if (activeLayers.includes(layer.id)) {
                    if (!map.getSource(srcId)) map.addSource(srcId, { type: 'raster', tiles: [`${layer.geoserverUrl}/wms?service=WMS&version=1.1.0&request=GetMap&layers=${layer.workspace}:${layer.layerName}&bbox={bbox-epsg-3857}&width=256&height=256&srs=EPSG:3857&format=image/png&transparent=true`], tileSize: 256 });
                    if (!map.getLayer(lyrId)) map.addLayer({ id: lyrId, type: 'raster', source: srcId, paint: { 'raster-opacity': 0.7 } });
                } else {
                    if (map.getLayer(lyrId)) map.removeLayer(lyrId);
                    if (map.getSource(srcId)) map.removeSource(srcId);
                }
            });
        };
        if (!map.isStyleLoaded()) map.once('load', () => apply(map));
        else apply(map);
    }, [activeLayers, filters.baseMap]); // eslint-disable-line

    // ── Neighborhood hover ──────────────────────────────────────
    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current.getMap();
        let current = null;
        let timer;
        const onMove = (e) => {
            if (!activeLayers.includes('geoServices_neighborhood') || isDrawing) return;
            if (timer) return;
            timer = setTimeout(async () => {
                timer = null;
                const layer = OVERLAY_LAYERS.find(l => l.id === 'geoServices_neighborhood');
                if (!layer) return;
                const { point } = e;
                const bounds = map.getBounds();
                const w = map.getContainer().clientWidth;
                const h = map.getContainer().clientHeight;
                const url = `${layer.geoserverUrl}/wms?service=WMS&version=1.1.0&request=GetFeatureInfo&layers=${layer.workspace}:${layer.layerName}&query_layers=${layer.workspace}:${layer.layerName}&bbox=${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}&width=${w}&height=${h}&srs=EPSG:4326&x=${Math.round(point.x)}&y=${Math.round(point.y)}&info_format=application/json&feature_count=1`;
                try {
                    const data = await (await apiRequest(url)).json();
                    if (data.features?.length > 0) {
                        const name = data.features[0].properties.name || data.features[0].properties.NAME || '';
                        if (name && name !== current) { current = name; map.getCanvas().style.cursor = 'pointer'; setNeighborhoodTooltip({ name, x: point.x, y: point.y }); }
                    } else if (current) { current = null; map.getCanvas().style.cursor = ''; setNeighborhoodTooltip(null); }
                } catch { if (current) { current = null; map.getCanvas().style.cursor = ''; setNeighborhoodTooltip(null); } }
            }, 200);
        };
        map.on('mousemove', onMove);
        return () => { map.off('mousemove', onMove); if (timer) clearTimeout(timer); setNeighborhoodTooltip(null); };
    }, [activeLayers, isDrawing]); // eslint-disable-line

    // ── Drawing controls ────────────────────────────────────────
    const startDraw = () => {
        setIsDrawing(true); setDrawingPoints([]);
        if (mapRef.current) { const m = mapRef.current.getMap(); m.dragPan.disable(); m.scrollZoom.disable(); m.doubleClickZoom.disable(); }
    };

    const stopDraw = () => {
        setIsDrawing(false); setIsMouseDown(false);
        if (mapRef.current) { const m = mapRef.current.getMap(); m.dragPan.enable(); m.scrollZoom.enable(); m.doubleClickZoom.enable(); }
        if (drawingPoints.length > 2) {
            const closed = [...drawingPoints, drawingPoints[0]];
            setDrawnArea(closed);
            if (drawRef.current) { drawRef.current.deleteAll(); drawRef.current.add({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [closed] } }); }
        }
        setTimeout(() => setDrawingPoints([]), 100);
    };

    const clearDraw = () => { setDrawnArea(null); setDrawingPoints([]); if (drawRef.current) drawRef.current.deleteAll(); };

    // ── Mouse events ────────────────────────────────────────────
    const onMouseDown = (e) => {
        if (routingState.isActive && (routingState.step === 'origin' || routingState.step === 'destination')) { handleRoutingClick(e); return; }
        if (distanceSearchState.isActive) { handleDistanceSearchClick(e); return; }
        if (isDrawing) { setIsMouseDown(true); const { lng, lat } = e.lngLat; setDrawingPoints(prev => [...prev, [lng, lat]]); }
    };

    const onMouseMove = (e) => { if (isDrawing && isMouseDown) { const { lng, lat } = e.lngLat; setDrawingPoints(prev => [...prev, [lng, lat]]); } };

    const onMouseUp = () => { if (isDrawing && isMouseDown) { setIsMouseDown(false); if (drawingPoints.length > 2) setTimeout(stopDraw, 100); } };

    // ── project helper for SVG overlays ─────────────────────────
    const project = (point) => {
        const map = mapRef.current?.getMap();
        if (!map) return null;
        return map.project(point);
    };

    const filteredListings = filterProperties(visibleProperties);

    // ── Apply geo-query conditions on top of regular filters ────
    const geoFilteredListings = geoQueryConditions.length === 0
        ? filteredListings
        : filteredListings.filter(listing => {
            const geoFeatures = listing.property?.geo_features || [];
            return geoQueryConditions.every(cond => {
                const gf = geoFeatures.find(f => f.id === cond.featureId);
                if (!gf || gf.value == null) return false;
                const isScore = cond.featureDef?.feature_type === 'score';
                // score: listing value must be >= threshold (higher = better)
                // distance/count: listing value must be <= threshold (lower = better)
                return isScore
                    ? parseFloat(gf.value) >= cond.threshold
                    : parseFloat(gf.value) <= cond.threshold;
            });
        });

    // Compute score range for normalisation when sortPriorities are active
    const rankedListings = aiMatch?.active
        ? rankListingsByAiMatch(geoFilteredListings, aiMatch)
        : geoFilteredListings;
    const scoredListings = rankedListings.map(l => ({
        listing: l,
        score: aiMatch?.active
            ? Number(l.ai_match_score) || 0
            : computeListingScore(l, sortPriorities),
    }));
    const hasScoring = aiMatch?.active || sortPriorities.length > 0;
    const maxScore = hasScoring
        ? Math.max(...scoredListings.map(s => s.score), 0.001)
        : 0;

    return (
        <div className="mc-root">
            {/* POI chip strip — centered top */}
            <POIControl
                isVisible={showPOIs}
                onToggle={setShowPOIs}
                onPOIsLoad={setPois}
                onPOISelect={setSelectedPOI}
                selectedPOI={selectedPOI}
                mapBounds={mapRef.current?.getMap()?.getBounds()}
            />

            <div className="mc-map-wrap">
                {/* ── Glass toolbar — top right ── */}
                <MapToolbar
                    showSearch={showSearch} setShowSearch={setShowSearch}
                    showLayerControl={showLayerControl} setShowLayerControl={setShowLayerControl}
                    activeLayers={activeLayers} setActiveLayers={setActiveLayers}
                    isDrawing={isDrawing} drawnArea={drawnArea}
                    onStartDraw={startDraw} onStopDraw={stopDraw} onClearDraw={clearDraw}
                    mapRef={mapRef} setViewState={setViewState}
                    onRoutingStateChange={setRoutingState}
                    onRouteDataChange={setRouteData}
                    onDistanceSearchStateChange={onDistanceSearchStateChange}
                    onDistanceBufferChange={onDistanceBufferChange}
                    onGeoFilterChange={onGeoFilterChange}
                    activeGeoFilters={filters?.geoFilters || []}
                    onGeoQueryConditionsChange={setGeoQueryConditions}
                    onGeoQueryPOIsChange={setGeoQueryPOIs}
                    onFlashBuffers={handleFlashBuffers}
                />

                {/* Layer panel */}
                {showLayerControl && (
                    <LayerPanel activeLayers={activeLayers} setActiveLayers={setActiveLayers} />
                )}

                {/* Drawing mode banner */}
                <DrawingBanner isDrawing={isDrawing} onCancel={stopDraw} />

                {/* Zoom badge */}
                <ZoomBadge zoom={viewState.zoom} />

                {/* Base map switcher */}
                <BaseMapSwitcher
                    currentMap={filters.baseMap}
                    onMapChange={(v) => {
                        setFilters(prev => ({ ...prev, baseMap: v }));
                        setViewState(prev => ({ ...prev, pitch: v === 'terrain3d' ? 60 : 0 }));
                    }}
                />

                {viewportDirty && !isDrawing && (
                    <button
                        type="button"
                        className="mc-search-area"
                        onClick={() => {
                            handleMapMove(mapRef);
                            setViewportDirty(false);
                        }}
                    >
                        جستجو در این محدوده
                    </button>
                )}

                {/* ── MapLibre map ── */}
                <Map
                    ref={mapRef}
                    {...viewState}
                    onMove={evt => setViewState(evt.viewState)}
                    onMoveEnd={() => {
                        if (suppressNextMoveEndRef.current) {
                            suppressNextMoveEndRef.current = false;
                            return;
                        }
                        if (!isDrawing) setViewportDirty(true);
                    }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    className={`mc-canvas ${isDrawing ? 'drawing' : ''}`}
                    mapStyle={MAP_STYLES[filters.baseMap]}
                    attributionControl={false}
                    interactiveLayerIds={isDrawing ? [] : undefined}
                    pitch={filters.baseMap === 'terrain3d' ? 60 : 0}
                    maxPitch={85}
                    minPitch={0}
                >
                    <NavigationControl position="bottom-left" />
                    <ScaleControl position="bottom-left" />
                    <GeolocateControl position="bottom-left" />

                    {/* ── Property markers ── */}
                    {scoredListings.map(({ listing, score }) => {
                        const loc = listing.property?.location;
                        if (!loc?.coordinates) return null;
                        const [lon, lat] = loc.coordinates;
                        const isSelected = selectedProperty?.listing_id === listing.listing_id;
                        const scoreColor = hasScoring
                            ? getScoreColor(maxScore > 0 ? score / maxScore : 0)
                            : null;
                        return (
                            <Marker key={listing.listing_id} longitude={lon} latitude={lat} anchor="bottom">
                                <PropertyMarker
                                    listing={listing}
                                    isSelected={isSelected}
                                    onClick={() => setSelectedProperty(isSelected ? null : listing)}
                                    scoreColor={scoreColor}
                                    rank={listing.ai_match_rank}
                                />
                            </Marker>
                        );
                    })}

                    {/* ── POI markers (POIControl) ── */}
                    {showPOIs && pois.map(poi => {
                        const geom = poi.geometry;
                        const props = poi.properties;
                        if (!geom?.coordinates?.[0]) return null;
                        const [lon, lat] = geom.coordinates[0];
                        return (
                            <Marker key={props.osm_id || poi.id} longitude={lon} latitude={lat} onClick={() => setSelectedPOI({ ...props, geometry: geom })}>
                                <div className={`mc-poi-dot ${selectedPOI?.osm_id === props.osm_id ? 'selected' : ''}`} />
                            </Marker>
                        );
                    })}

                    {/* ── GeoQuery POI markers ── */}
                    {geoQueryPOIs.map((poi, idx) => {
                        const geom = poi.geometry;
                        const props = poi.properties || {};
                        if (!geom?.coordinates?.[0]) return null;
                        const [lon, lat] = geom.coordinates[0];
                        return (
                            <Marker
                                key={`gq-${props.osm_id || idx}`}
                                longitude={lon}
                                latitude={lat}
                                onClick={() => setSelectedPOI({ ...props, geometry: geom })}
                            >
                                <div
                                    className={`mc-poi-dot geoquery ${selectedPOI?.osm_id === props.osm_id ? 'selected' : ''}`}
                                    style={{
                                        opacity: poisFading ? 0 : 1,
                                        transition: 'opacity 1s ease-out',
                                    }}
                                />
                            </Marker>
                        );
                    })}

                    {/* ── Property popup ── */}
                    {selectedProperty?.property?.location && (
                        <Popup
                            longitude={selectedProperty.property.location.coordinates[0]}
                            latitude={selectedProperty.property.location.coordinates[1]}
                            onClose={() => setSelectedProperty(null)}
                            closeButton={false}
                            closeOnClick={false}
                            anchor="bottom"
                            offset={20}
                            className="mc-popup-wrapper"
                        >
                            <PropertyPopup listing={selectedProperty} onClose={() => setSelectedProperty(null)} />
                        </Popup>
                    )}

                    {/* ── POI popup ── */}
                    {selectedPOI?.geometry?.coordinates?.[0] && (
                        <Popup
                            longitude={selectedPOI.geometry.coordinates[0][0]}
                            latitude={selectedPOI.geometry.coordinates[0][1]}
                            onClose={() => setSelectedPOI(null)}
                            closeButton={true}
                            closeOnClick={false}
                            anchor="bottom"
                        >
                            <div className="mc-poi-popup" dir="rtl">
                                <strong>{selectedPOI.name || 'POI'}</strong>
                                <p>{selectedPOI.type}</p>
                                {selectedPOI.is_verified && <span className="mc-poi-verified">✓ تأیید شده</span>}
                            </div>
                        </Popup>
                    )}

                    {/* ── Routing markers ── */}
                    {routingState.origin && (
                        <Marker longitude={routingState.origin.lng} latitude={routingState.origin.lat}>
                            <div className="mc-route-dot origin">A</div>
                        </Marker>
                    )}
                    {routingState.destination && (
                        <Marker longitude={routingState.destination.lng} latitude={routingState.destination.lat}>
                            <div className="mc-route-dot destination">B</div>
                        </Marker>
                    )}
                    {routeData && (
                        <>
                            <Marker longitude={routeData.origin.lng} latitude={routeData.origin.lat}><div className="mc-route-dot origin">A</div></Marker>
                            <Marker longitude={routeData.destination.lng} latitude={routeData.destination.lat}><div className="mc-route-dot destination">B</div></Marker>
                            <Popup longitude={(routeData.origin.lng + routeData.destination.lng) / 2} latitude={(routeData.origin.lat + routeData.destination.lat) / 2} closeButton={false} closeOnClick={false} anchor="bottom">
                                <div className="mc-route-info" dir="rtl">
                                    <span>🛣 {(routeData.distance / 1000).toFixed(1)} کیلومتر</span>
                                    <span>⏱ {Math.round(routeData.duration / 60)} دقیقه</span>
                                </div>
                            </Popup>
                        </>
                    )}

                    {/* ── Drawing preview SVG ── */}
                    {isDrawing && drawingPoints.length > 0 && (
                        <svg key={`draw-${mapKey}`} className="mc-svg-overlay">
                            <polyline
                                points={drawingPoints.map(p => { const pt = project(p); return pt ? `${pt.x},${pt.y}` : ''; }).join(' ')}
                                fill="none" stroke="#6366f1" strokeWidth="2.5" strokeOpacity="0.9" strokeDasharray="6,4" strokeLinecap="round"
                            />
                            {drawingPoints.map((p, i) => { const pt = project(p); return pt ? <circle key={i} cx={pt.x} cy={pt.y} r="3" fill="#6366f1" fillOpacity="0.7" /> : null; })}
                        </svg>
                    )}

                    {/* ── Drawn area SVG ── */}
                    {!isDrawing && drawnArea?.length > 0 && (
                        <svg key={`area-${mapKey}`} className="mc-svg-overlay">
                            <polygon
                                points={drawnArea.map(p => { const pt = project(p); return pt ? `${pt.x},${pt.y}` : ''; }).join(' ')}
                                fill="rgba(99,102,241,0.12)" stroke="#6366f1" strokeWidth="2" strokeOpacity="0.8" strokeDasharray="6,3"
                            />
                        </svg>
                    )}

                    {/* ── Route line SVG ── */}
                    {routeData?.geometry?.coordinates && (
                        <svg key={`route-${mapKey}`} className="mc-svg-overlay">
                            <polyline
                                points={routeData.geometry.coordinates.map(c => { const pt = project([c[0], c[1]]); return pt ? `${pt.x},${pt.y}` : ''; }).join(' ')}
                                fill="none" stroke="#3b82f6" strokeWidth="4" strokeOpacity="0.85" strokeLinecap="round" strokeLinejoin="round"
                            />
                        </svg>
                    )}

                    {/* ── Distance buffer SVG ── */}
                    {distanceBuffer?.polygon?.length > 0 && (() => {
                        const map = mapRef.current?.getMap();
                        if (!map) return null;
                        const isNet = distanceBuffer.type === 'network';
                        const color = isNet ? '#3b82f6' : '#22c55e';
                        const fill = isNet ? 'rgba(59,130,246,0.15)' : 'rgba(34,197,94,0.15)';
                        const pts = distanceBuffer.polygon.map(p => { const pt = project(p); return pt ? `${pt.x},${pt.y}` : '0,0'; }).filter(p => p !== '0,0').join(' ');
                        const center = distanceBuffer.center;
                        const cp = center ? project([center.lng, center.lat]) : null;
                        return (
                            <svg key={`buf-${mapKey}-${viewState.zoom}`} className="mc-svg-overlay">
                                <defs>
                                    <filter id="buf-glow"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                                </defs>
                                <polygon points={pts} fill={fill} stroke={color} strokeWidth="2" strokeOpacity="0.9" filter="url(#buf-glow)" />
                                {cp && (
                                    <g>
                                        <circle cx={cp.x} cy={cp.y} r="14" fill={fill} stroke={color} strokeWidth="1.5" strokeDasharray="3,3" />
                                        <circle cx={cp.x} cy={cp.y} r="6" fill={color} stroke="white" strokeWidth="2" />
                                        <circle cx={cp.x} cy={cp.y} r="2" fill="white" />
                                    </g>
                                )}
                            </svg>
                        );
                    })()}

                    {/* ── GeoQuery flash buffers ── */}
                </Map>

                {/* ── GeoQuery flash buffers — outside <Map> so re-renders don't reset CSS transitions ── */}
                {flashBuffers.map(({ key, rings, fading }) => (
                    <svg
                        key={key}
                        className="mc-svg-overlay"
                        style={{
                            opacity: fading ? 0 : 1,
                            transition: 'opacity 1s ease-out',
                            pointerEvents: 'none',
                        }}
                    >
                        <defs>
                            <filter id="gq-buf-glow">
                                <feGaussianBlur stdDeviation="5" result="b" />
                                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                            </filter>
                        </defs>
                        {rings.map((ring, i) => {
                            const pts = ring
                                .map(p => { const pt = project(p); return pt ? `${pt.x},${pt.y}` : null; })
                                .filter(Boolean)
                                .join(' ');
                            return pts ? (
                                <polygon
                                    key={i}
                                    points={pts}
                                    fill="rgba(79,70,229,0.12)"
                                    stroke="#4f46e5"
                                    strokeWidth="1.5"
                                    strokeDasharray="5,3"
                                    filter="url(#gq-buf-glow)"
                                />
                            ) : null;
                        })}
                    </svg>
                ))}

                {/* Neighborhood tooltip */}
                {neighborhoodTooltip && (
                    <div className="mc-neighborhood-tip" style={{ left: neighborhoodTooltip.x + 12, top: neighborhoodTooltip.y - 12 }}>
                        {neighborhoodTooltip.name}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MapContainer;
